import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons as I } from '../pack-icons';
import { BrandIcons } from '../pack-icons/brands';
import EngagementChart from './EngagementChart.jsx';
import MetricSeriesChart from '../metrics/MetricSeriesChart.jsx';
import { MetricSeriesTable } from '../metrics/MetricValue.jsx';
import { aggregateMetricSeries, formatMetric, metricNumber, metricSegments, nullableSum } from '../../lib/socialMetrics.js';
import { platformDisplayName } from '../../lib/platforms.js';

/* ============================================================
   AnalyticsPanel — tabbed multi-metric analytics for the dashboard.
   Ported from design/pages/dashboard-analytics.jsx; demo datasets
   replaced by real props fed from /api/analytics/overview (+ best-times,
   + audience once follower tracking lands). Every view degrades to a
   graceful empty state when its series has no data yet.
   ============================================================ */

function fmtN(n) {
  return formatMetric(n);
}

function signedNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function smoothPath(pts) {
  if (pts.length < 2) return '';
  let p = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = pts[Math.max(0, i - 1)];
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[i + 1];
    const [x3, y3] = pts[Math.min(pts.length - 1, i + 2)];
    const cp1x = x1 + (x2 - x0) / 6, cp1y = y1 + (y2 - y0) / 6;
    const cp2x = x2 - (x3 - x1) / 6, cp2y = y2 - (y3 - y1) / 6;
    p += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x2} ${y2}`;
  }
  return p;
}

function shortDate(iso, locale = 'en') {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function overviewChartData(engagementData, reach, overviewDaily, locale) {
  const dated = new Map();
  const undated = [];
  for (const row of engagementData || []) {
    const point = { ...row, label: row.date ? shortDate(row.date, locale) : row.label, engagement: metricNumber(row.engagement), published: metricNumber(row.published), views: null };
    if (row.date) dated.set(row.date, point);
    else undated.push(point);
  }
  for (const row of reach || []) {
    if (!row.date) continue; // Without a date, a views point cannot be safely aligned.
    const point = dated.get(row.date) || { date: row.date, label: shortDate(row.date, locale), engagement: null, published: null, views: null };
    dated.set(row.date, { ...point, views: metricNumber(row.views) });
  }
  // Keep the plotted post count paired with Views from the same overview snapshot.
  // A separate posts-summary request may complete on either side of a publication.
  for (const row of overviewDaily || []) {
    if (!row.date) continue;
    const point = dated.get(row.date) || { date: row.date, label: shortDate(row.date, locale), engagement: null, published: null, views: null };
    dated.set(row.date, { ...point, published: metricNumber(row.published) });
  }
  // Never position undated legacy points by index beside dated observations.
  return dated.size ? [...dated.values()].sort((a, b) => a.date.localeCompare(b.date)) : undated;
}

function EmptyView({ icon: Icon, title, hint }) {
  return (
    <div className="an-empty">
      {Icon && <Icon size={22} />}
      <div className="an-empty-t">{title}</div>
      {hint && <div className="an-empty-h">{hint}</div>}
    </div>
  );
}

/* ---------- Platform performance (horizontal bars + sub-tabs) ---------- */
function PlatformView({ data }) {
  const { t } = useTranslation();
  const [metric, setMetric] = useState('engagement');
  const rows = data || [];
  const labels = { engagement: t('analyticsPanel.engagementRate'), views: t('socialMetrics.views'), reach: t('analyticsPanel.totalReach'), posts: t('analyticsPanel.postsPublished') };
  const sorted = useMemo(() => [...rows].sort((a, b) => (metricNumber(b[metric]) ?? -1) - (metricNumber(a[metric]) ?? -1)), [rows, metric]);
  if (!rows.length) {
    return <EmptyView icon={I.grid} title={t('analyticsPanel.noPlatformDataTitle')} hint={t('analyticsPanel.noPlatformDataHint')} />;
  }
  const max = Math.max(1, ...sorted.map((p) => p[metric] || 0));
  return (
    <div>
      <div className="an-subtabs">
        {['engagement', 'views', 'reach', 'posts'].map((m) => (
          <button key={m} className={metric === m ? 'is-active' : ''} onClick={() => setMetric(m)} type="button">{labels[m]}</button>
        ))}
      </div>
      <div className="an-bars">
        {sorted.map((p) => {
          const val = metricNumber(p[metric]);
          const pct = val === null ? 0 : (val / max) * 100;
          const display = val === null ? '—' : metric === 'engagement' ? val.toFixed(1) + '%' : fmtN(val);
          return (
            <div key={p.platform} className="an-bar-row">
              <span className={`an-bar-ic ob-net-ic-${p.platform}`}>{BrandIcons[p.platform]?.({ size: 12 })}</span>
              <span className="an-bar-name">{platformDisplayName(p.platform)}</span>
              <div className="an-bar-track">
                <div className="an-bar-fill" style={{ width: pct + '%', background: 'linear-gradient(90deg, var(--grad-purple), var(--grad-magenta))' }} />
              </div>
              <span className="an-bar-val mono" aria-label={val === null ? t('analyticsPanel.platformMetricMissing', { metric: labels[metric] }) : undefined}>{display}</span>
            </div>
          );
        })}
      </div>
      {sorted.some(p => metricNumber(p[metric]) === null) && <p className="metric-feedback">{t('analyticsPanel.platformMetricCoverage')}</p>}
    </div>
  );
}

/* ---------- Interaction mix (stacked bars per week) ---------- */
function InteractionView({ data }) {
  const { t } = useTranslation();
  const [activeWeek, setActiveWeek] = useState(null);
  const keys = [
    { k: 'reactionsTotal', label: t('socialMetrics.reactionsTotal'), color: 'var(--grad-purple)' },
    { k: 'saves', label: t('analyticsPanel.saves'), color: 'var(--grad-cyan)' },
    { k: 'comments', label: t('analyticsPanel.comments'), color: 'var(--grad-magenta)' },
    { k: 'shares', label: t('analyticsPanel.shares'), color: 'var(--success)' },
  ];
  // Bucket the selected period into weekly columns.
  const weeks = useMemo(() => {
    const days = data || [];
    if (!days.length) return [];
    const recent = days.map((day) => (
      day && Object.prototype.hasOwnProperty.call(day, 'reactions')
        ? day
        : { ...day, reactions: day?.likes }
    ));
    const out = [];
    for (let i = 0; i < recent.length; i += 7) {
      const chunk = recent.slice(i, i + 7);
      const agg = { label: `W${out.length + 1}`, ...aggregateMetricSeries(chunk) };
      out.push(agg);
    }
    return out;
  }, [data]);
  const grand = nullableSum(weeks.flatMap(w => keys.map(kk => w[kk.k])));
  if (!weeks.length) {
    return <EmptyView icon={I.heart} title={t('analyticsPanel.noInteractionsTitle')} hint={t('analyticsPanel.noInteractionsHint')} />;
  }
  const totals = weeks.map((w) => nullableSum(keys.map(kk => w[kk.k])));
  const max = Math.max(1, ...totals) * 1.1;
  return (
    <div>
      <div className="an-stats">
        <div><div className="an-cs-l">{t('analyticsPanel.totalInteractions')}</div><div className="an-cs-v">{fmtN(grand)}</div></div>
        <div className="an-legend" style={{ flexWrap: 'wrap' }}>
          {keys.map((kk) => <span key={kk.k} className="an-leg"><i style={{ background: kk.color }} />{kk.label}</span>)}
        </div>
      </div>
      {weeks.some(w => keys.some(kk => w.metricAvailability?.[kk.k]?.partial)) && <p className="metric-feedback">{t('socialMetrics.partial')}</p>}
      <div className="an-stack">
        {weeks.map((w, wi) => {
          const total = nullableSum(keys.map(kk => w[kk.k]));
          return (
            <button key={wi} type="button" className="an-stack-col" onMouseEnter={() => setActiveWeek(wi)} onFocus={() => setActiveWeek(wi)} onClick={() => setActiveWeek(wi)} aria-pressed={activeWeek === wi} aria-label={t('analyticsPanel.weekTotal', { weekLabel: w.label, count: fmtN(total) })}>
              <div className="an-stack-bar" style={{ height: ((total ?? 0) / max) * 150 + 'px' }}>
                {keys.filter(kk => w[kk.k] > 0).map((kk) => (
                  <div key={kk.k} className="an-stack-seg" style={{ flex: w[kk.k], background: kk.color }} title={t('analyticsPanel.segmentTitle', { label: kk.label, count: w[kk.k] })} />
                ))}
              </div>
              <span className="an-stack-l mono">{w.label}</span>
              <span className="metric-note">{fmtN(total)}</span>
            </button>
          );
        })}
      </div>
      {activeWeek !== null && weeks[activeWeek] && <div className="an-stack-detail" aria-live="polite">
        <div className="an-tip-l">{weeks[activeWeek].label} · {fmtN(totals[activeWeek])}</div>
        <div className="an-stack-detail-rows">
          {keys.map((kk) => <div key={kk.k} className="an-tip-row"><i style={{ background: kk.color }} /><span>{kk.label}</span><b className="mono">{fmtN(weeks[activeWeek][kk.k])}</b></div>)}
        </div>
      </div>}
      <MetricSeriesTable data={weeks} fields={keys.map(kk => ({ key: kk.k, label: kk.label }))} label={t('analyticsPanel.interactions')} />
    </div>
  );
}

/* ---------- Audience growth (followers) ---------- */
function AudienceView({ data }) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(null);
  if (!data?.length) {
    return <EmptyView icon={I.users} title={t('analyticsPanel.noFollowerDataTitle')} hint={t('analyticsPanel.noFollowerDataHint')} />;
  }
  const W = 760, H = 220, padX = 28, padY = 22;
  const innerW = W - padX * 2, innerH = H - padY * 2;
  const measured = data.map(d => metricNumber(d.total)).filter(value => value !== null);
  const min = measured.length ? Math.min(...measured) : 0;
  const max = measured.length ? Math.max(...measured) : 0;
  const span = (max - min) || 1;
  const xy = (i, v) => [padX + (i / Math.max(1, data.length - 1)) * innerW, padY + innerH - ((v - min) / span) * innerH * 0.9 - innerH * 0.05];
  const segments = metricSegments(data, 'total', xy);
  const measuredDeltas = data.map(d => signedNumber(d.delta)).filter(value => value !== null);
  const net = measuredDeltas.length ? measuredDeltas.reduce((sum, value) => sum + value, 0) : null;
  const firstDay = Date.parse(`${data[0]?.date}T00:00:00Z`);
  const lastDay = Date.parse(`${data[data.length - 1]?.date}T00:00:00Z`);
  const elapsedDays = Number.isFinite(firstDay) && Number.isFinite(lastDay)
    ? Math.max(1, Math.round((lastDay - firstDay) / 86400000)) : Math.max(1, measuredDeltas.length);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * W - padX;
    setHover(Math.max(0, Math.min(data.length - 1, Math.round(x / (innerW / Math.max(1, data.length - 1))))));
  };
  return (
    <div>
      <div className="an-stats">
        <div><div className="an-cs-l">{t('analyticsPanel.followers')}</div><div className="an-cs-v">{fmtN(data[data.length - 1].total)}{net !== null && net !== 0 && <span className={`an-cs-delta ${net > 0 ? 'up' : 'down'}`}>{net > 0 ? <I.arrowUpR size={11} /> : <I.arrowDownR size={11} />}{net < 0 ? '-' : '+'}{fmtN(Math.abs(net))}</span>}</div></div>
        <div><div className="an-cs-l">{t('analyticsPanel.avgDailyGrowth')}</div><div className="an-cs-v">{net === null ? '—' : `${net >= 0 ? '+' : ''}${(net / elapsedDays).toFixed(1)}`}</div></div>
        <div className="an-legend"><span className="an-leg"><i style={{ background: 'var(--grad-magenta)' }} />{t('analyticsPanel.totalFollowers')}</span></div>
      </div>
      <div className="an-svg-wrap" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="an-svg">
          <defs>
            <linearGradient id="anAudArea" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="var(--grad-magenta)" stopOpacity="0.2" />
              <stop offset="100%" stopColor="var(--grad-magenta)" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="anAudLine" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="var(--grad-purple)" />
              <stop offset="100%" stopColor="var(--grad-magenta)" />
            </linearGradient>
          </defs>
          {[0, 1, 2, 3, 4].map((i) => {
            const y = padY + (i / 4) * innerH;
            return <line key={i} x1={padX} x2={W - padX} y1={y} y2={y} stroke="var(--border-subtle)" strokeWidth="1" strokeDasharray={i === 4 ? '0' : '2 3'} />;
          })}
          {segments.filter(points => points.length > 1).map((points, index) => <g key={index}>
            <path d={`${smoothPath(points)} L ${points[points.length - 1][0]} ${padY + innerH} L ${points[0][0]} ${padY + innerH} Z`} fill="url(#anAudArea)" />
            <path d={smoothPath(points)} fill="none" stroke="url(#anAudLine)" strokeWidth="2.6" strokeLinecap="round" />
          </g>)}
          {segments.flatMap(points => points.length === 1 ? points : []).map(([x, y], index) => <circle key={index} cx={x} cy={y} r="3" fill="var(--grad-magenta)" />)}
          {hover != null && metricNumber(data[hover]?.total) !== null && (() => {
            const [hx, hy] = xy(hover, data[hover].total);
            return <g>
              <line x1={hx} x2={hx} y1={padY} y2={padY + innerH} stroke="var(--grad-magenta)" strokeWidth="1" strokeDasharray="2 3" opacity="0.5" />
              <circle cx={hx} cy={hy} r="4.5" fill="var(--bg-elev)" stroke="var(--grad-magenta)" strokeWidth="2" />
            </g>;
          })()}
        </svg>
        {hover != null && data[hover] && (() => {
          const left = ((padX + (hover / Math.max(1, data.length - 1)) * innerW) / W) * 100;
          return <div className="an-tip" style={{ left: left + '%', transform: `translateX(-${left}%)` }}>
            <div className="an-tip-l">{shortDate(data[hover].date)}</div>
            <div className="an-tip-row"><i style={{ background: 'var(--grad-magenta)' }} /><span>{t('analyticsPanel.total')}</span><b className="mono">{fmtN(data[hover].total)}</b></div>
            <div className="an-tip-row"><i style={{ background: 'var(--success)' }} /><span>{t('analyticsPanel.thatDay')}</span><b className="mono">{signedNumber(data[hover].delta) === null ? '—' : `${data[hover].delta >= 0 ? '+' : ''}${data[hover].delta}`}</b></div>
          </div>;
        })()}
      </div>
    </div>
  );
}

/* ---------- Best time heatmap ---------- */
function HeatmapView({ data }) {
  const { t } = useTranslation();
  // data: { days:[...], buckets:[...], grid:[{day,hour,value}], best? }
  if (!data?.grid?.length) {
    return <EmptyView icon={I.clock} title={t('analyticsPanel.noTimingDataTitle')} hint={t('analyticsPanel.noTimingDataHint')} />;
  }
  const { days, buckets, grid, best } = data;
  const cell = (d, h) => grid.find((g) => g.day === d && g.hour === h);
  return (
    <div>
      <div className="an-stats">
        <div><div className="an-cs-l">{t('analyticsPanel.bestWindow')}</div><div className="an-cs-v" style={{ fontSize: '18px' }}>{best || '—'}</div></div>
        <div className="an-legend">
          <span className="an-leg" style={{ gap: '4px' }}>{t('analyticsPanel.low')}</span>
          <span className="an-heat-scale">
            <i style={{ background: 'color-mix(in srgb, var(--accent) 12%, var(--bg-subtle))' }} />
            <i style={{ background: 'color-mix(in srgb, var(--accent) 40%, var(--bg-subtle))' }} />
            <i style={{ background: 'color-mix(in srgb, var(--accent) 70%, var(--bg-subtle))' }} />
            <i style={{ background: 'var(--accent)' }} />
          </span>
          <span className="an-leg">{t('analyticsPanel.high')}</span>
        </div>
      </div>
      <div className="an-heat" style={{ gridTemplateColumns: `auto repeat(${buckets.length}, 1fr)` }}>
        <div className="an-heat-corner" />
        {buckets.map((b) => <div key={b} className="an-heat-htop mono">{b}:00</div>)}
        {days.map((d) => (
          <React.Fragment key={d}>
            <div className="an-heat-day mono">{d}</div>
            {buckets.map((b) => {
              const c = cell(d, b);
              const v = c?.value || 0;
              return <div key={b} className="an-heat-cell"
                style={{ background: `color-mix(in srgb, var(--accent) ${Math.round(v * 82)}%, var(--bg-subtle))` }}
                title={`${d} ${b}:00 · ${Math.round(v * 100)}%`}>
                {v > 0.78 && <span className="an-heat-star" />}
              </div>;
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/* ---------- Top-level tabbed panel ---------- */
export default function AnalyticsPanel({
  engagementData = [],
  overviewDaily = [],
  engagementMode = 'rate',
  reach = [],
  platforms = [],
  interactions = [],
  audience = [],
  bestTimes = null,
  partial = false,
  periodSummary = null,
  rangeDays = 30,
  onRangeChange,
  locked = false,
  onUnlock,
}) {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState('engagement');
  const [overviewMetric, setOverviewMetric] = useState('views');
  const tabs = [
    { id: 'engagement', label: t('analyticsPanel.overview'), icon: I.trend },
    { id: 'reach', label: t('socialMetrics.views'), icon: I.eye },
    { id: 'platforms', label: t('analyticsPanel.platforms'), icon: I.grid },
    { id: 'interactions', label: t('analyticsPanel.interactions'), icon: I.heart },
    { id: 'audience', label: t('analyticsPanel.audience'), icon: I.users },
    { id: 'timing', label: t('analyticsPanel.bestTime'), icon: I.clock },
  ];
  const active = tabs.find((t) => t.id === tab) || tabs[0];
  const publishedTargets = periodSummary?.publishedTargets;
  const measuredTargets = engagementMode === 'rate'
    ? periodSummary?.engagementRateMeasuredTargets
    : periodSummary?.interactionsMeasuredTargets;
  const hasCoverage = Number.isFinite(publishedTargets) && publishedTargets > 0 && Number.isFinite(measuredTargets);
  const chartData = useMemo(() => overviewChartData(engagementData, reach, overviewDaily, i18n.language), [engagementData, reach, overviewDaily, i18n.language]);

  return (
    <div className="card an-card">
      <div className="an-head">
        <div>
          <div className="eyebrow"><span>{t('analyticsPanel.analytics')}</span></div>
          <h3 className="an-title">{active.label}</h3>
        </div>
        <div className="an-head-actions">
          <div className="chart-ranges" role="group" aria-label={t('analyticsPanel.analytics')}>
            {[30, 90].map(days => <button key={days} type="button" disabled={locked} className={rangeDays === days ? 'is-active' : ''} aria-pressed={rangeDays === days} onClick={() => onRangeChange?.(days)}>{days}d</button>)}
          </div>
        </div>
      </div>

      <div className="an-tabs">
        {tabs.map((t) => (
          <button key={t.id} className={`an-tab ${tab === t.id ? 'is-active' : ''}`} disabled={locked} onClick={() => setTab(t.id)} type="button">
            <t.icon size={13} />
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      <div className="an-body">
        {locked ? <>
          <EmptyView icon={I.lock} title={t('analyticsPanel.lockedTitle')} hint={t('analyticsPanel.lockedHint')} />
          <button type="button" className="btn btn-ghost btn-sm" onClick={onUnlock}>{t('freePublishing.viewPlans')} <I.arrowRight size={12} /></button>
        </> : <>
        {tab === 'engagement' && <>
          <div className="an-subtabs an-overview-modes" role="group" aria-label={t('engagementChart.chartMetric')}>
            {['views', 'engagement'].map(metric => <button key={metric} type="button" className={overviewMetric === metric ? 'is-active' : ''} aria-pressed={overviewMetric === metric} onClick={() => setOverviewMetric(metric)}>{metric === 'views' ? t('socialMetrics.views') : t('analyticsPanel.engagement')}</button>)}
          </div>
          <EngagementChart data={chartData} metric={overviewMetric} valueMode={engagementMode} partial={partial} embedded rangeDays={rangeDays} summaryValue={engagementMode === 'rate' ? periodSummary?.avgEngagementRate : periodSummary?.totalInteractions} reportedViews={periodSummary?.nativeViewsTotal} viewsMeasuredTargets={periodSummary?.nativeViewsMeasuredTargets} publishedTargets={publishedTargets} measuredTargets={measuredTargets} hasCoverage={hasCoverage} />
        </>}
        {tab === 'reach' && <MetricSeriesChart data={reach} />}
        {tab === 'platforms' && <PlatformView data={platforms} />}
        {tab === 'interactions' && <InteractionView data={interactions} />}
        {tab === 'audience' && <AudienceView data={audience} />}
        {tab === 'timing' && <HeatmapView data={bestTimes} />}
        </>}
      </div>
    </div>
  );
}
